import React, { useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import { 
  FiClock, FiShield, FiKey, FiHardDrive, FiWifi, FiDownload, 
  FiLock, FiPrinter, FiStar, FiUserPlus, FiAlertTriangle, 
  FiRefreshCw, FiChevronDown, FiCpu, FiUser, FiCloud, 
  FiLayers, FiDatabase, FiSmartphone, FiPhone, FiServer, FiMonitor,
  FiX
} from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';

const priorityColors = {
  Low: 'bg-slate-100 text-slate-700 border-slate-200',
  Medium: 'bg-blue-50 text-blue-700 border-blue-100',
  High: 'bg-orange-50 text-orange-700 border-orange-100',
  Critical: 'bg-red-600 text-white border-red-700 font-bold shadow-lg shadow-red-200 animate-pulse'
};

const priorityWeight = { Critical: 4, High: 3, Medium: 2, Low: 1 };

const categoryConfig = {
  'Security': { icon: FiShield, color: 'text-red-500', bg: 'bg-red-50' },
  'Access': { icon: FiKey, color: 'text-emerald-500', bg: 'bg-emerald-50' },
  'Hardware': { icon: FiHardDrive, color: 'text-slate-500', bg: 'bg-slate-100' },
  'Network': { icon: FiWifi, color: 'text-blue-500', bg: 'bg-blue-50' },
  'Software': { icon: FiDownload, color: 'text-indigo-500', bg: 'bg-indigo-50' },
  'Password': { icon: FiLock, color: 'text-orange-500', bg: 'bg-orange-50' },
  'Printer': { icon: FiPrinter, color: 'text-teal-500', bg: 'bg-teal-50' },
  'VIP': { icon: FiStar, color: 'text-purple-500', bg: 'bg-purple-50' },
  'Onboarding': { icon: FiUserPlus, color: 'text-pink-500', bg: 'bg-pink-50' },
  'Server Admin': { icon: FiServer, color: 'text-slate-600', bg: 'bg-slate-100' },
  'Identity': { icon: FiUser, color: 'text-emerald-600', bg: 'bg-emerald-100' },
  'M365': { icon: FiCloud, color: 'text-blue-600', bg: 'bg-blue-100' },
  'Virtualization': { icon: FiLayers, color: 'text-indigo-600', bg: 'bg-indigo-100' },
  'Database': { icon: FiDatabase, color: 'text-orange-600', bg: 'bg-orange-100' },
  'Cloud': { icon: FiCloud, color: 'text-sky-500', bg: 'bg-sky-50' },
  'MDM': { icon: FiSmartphone, color: 'text-teal-600', bg: 'bg-teal-100' },
  'Storage': { icon: FiHardDrive, color: 'text-slate-700', bg: 'bg-slate-200' },
  'Endpoint': { icon: FiMonitor, color: 'text-purple-600', bg: 'bg-purple-100' },
  'Telephony': { icon: FiPhone, color: 'text-green-600', bg: 'bg-green-100' },
  'Infrastructure': { icon: FiCpu, color: 'text-red-600', bg: 'bg-red-100' }
};

const TicketQueue = ({ tickets, onSelectTicket, onResetQueue, view, currentTime }) => {
  const [sortConfig, setSortConfig] = useState({ key: 'openedAt', direction: 'desc' });
  const [showResetWarning, setShowResetWarning] = useState(false);

  const getRemainingMinutes = (ticket) => {
    const elapsedMs = currentTime - ticket.openedAt;
    const elapsedMinutes = Math.floor(elapsedMs / 60000);
    return ticket.slaMinutes - elapsedMinutes;
  };

  const getSlaStatus = (ticket) => {
    if (view === 'resolved') return { text: 'Resolved', color: 'text-emerald-600' };
    const rem = getRemainingMinutes(ticket);
    if (rem < 0) return { text: 'BREACHED', color: 'text-red-600 font-bold', icon: FiAlertTriangle };
    return { text: `${rem}m`, color: rem < 15 ? 'text-orange-600 animate-pulse' : 'text-slate-500' };
  };

  const sortedTickets = [...tickets]
    .filter(t => view === 'resolved' ? t.status === 'resolved' : t.status === 'open')
    .sort((a, b) => {
      if (sortConfig.key === 'priority') return (priorityWeight[b.priority] - priorityWeight[a.priority]) * (sortConfig.direction === 'desc' ? 1 : -1);
      return (b.openedAt - a.openedAt) * (sortConfig.direction === 'desc' ? 1 : -1);
    });

  const handleConfirmReset = () => {
    onResetQueue();
    setShowResetWarning(false);
  };

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-slate-800 capitalize">{view} Worklist</h1>
          <p className="text-xs md:text-sm text-slate-500">Managing {sortedTickets.length} active incidents</p>
        </div>
        
        <div className="flex items-center space-x-3">
          {view !== 'resolved' && (
            <button 
              onClick={() => setShowResetWarning(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-slate-900 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-800 transition-all shadow-lg"
            >
              <SafeIcon icon={FiRefreshCw} />
              <span>Reset Session SLA</span>
            </button>
          )}
          <div className="flex items-center space-x-2 bg-white px-4 py-2 rounded-xl border border-slate-200">
            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Sort:</span>
            <select 
              className="text-xs font-bold bg-transparent outline-none"
              value={sortConfig.key}
              onChange={(e) => setSortConfig({ ...sortConfig, key: e.target.value })}
            >
              <option value="openedAt">Recent</option>
              <option value="priority">Priority</option>
            </select>
          </div>
        </div>
      </div>

      <div className="space-y-3 md:space-y-0">
        <div className="hidden md:block bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full text-left">
            <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase font-black text-slate-400 tracking-widest">
              <tr>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4">Requester</th>
                <th className="px-6 py-4">Subject</th>
                <th className="px-6 py-4">Priority</th>
                <th className="px-6 py-4">SLA</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {sortedTickets.map(ticket => {
                const sla = getSlaStatus(ticket);
                const cat = categoryConfig[ticket.category] || categoryConfig['Infrastructure'];
                return (
                  <tr key={ticket.id} onClick={() => onSelectTicket(ticket)} className="hover:bg-slate-50 cursor-pointer transition-colors">
                    <td className="px-6 py-4"><div className={`w-2 h-2 rounded-full ${ticket.status === 'resolved' ? 'bg-emerald-500' : 'bg-blue-500'}`} /></td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-3">
                        <img src={ticket.avatar} className="w-8 h-8 rounded-full border border-slate-200" alt="" />
                        <span className="text-sm font-bold text-slate-700">{ticket.user}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col">
                        <span className="text-xs font-black text-slate-400 uppercase tracking-widest flex items-center">
                          <SafeIcon icon={cat.icon} className={`mr-1 ${cat.color}`} />
                          {ticket.category}
                        </span>
                        <span className="text-sm font-bold text-slate-800 line-clamp-1">{ticket.title}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-tighter border ${priorityColors[ticket.priority]}`}>{ticket.priority}</span>
                    </td>
                    <td className="px-6 py-4 text-xs font-mono font-bold tracking-tight">
                      <span className={sla.color}>{sla.text}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="md:hidden space-y-3">
          {sortedTickets.map(ticket => {
            const sla = getSlaStatus(ticket);
            const cat = categoryConfig[ticket.category] || categoryConfig['Infrastructure'];
            return (
              <motion.div 
                key={ticket.id} 
                whileTap={{ scale: 0.98 }}
                onClick={() => onSelectTicket(ticket)}
                className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm space-y-3"
              >
                <div className="flex justify-between items-start">
                  <div className="flex items-center space-x-3">
                    <img src={ticket.avatar} className="w-10 h-10 rounded-xl" alt="" />
                    <div>
                      <div className="text-sm font-black text-slate-800">{ticket.user}</div>
                      <div className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">{ticket.department}</div>
                    </div>
                  </div>
                  <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-tighter border ${priorityColors[ticket.priority]}`}>{ticket.priority}</span>
                </div>
                <div>
                  <div className="flex items-center space-x-2 mb-1">
                    <SafeIcon icon={cat.icon} className={`text-xs ${cat.color}`} />
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{ticket.category}</span>
                  </div>
                  <h3 className="text-sm font-bold text-slate-700 leading-snug">{ticket.title}</h3>
                </div>
                <div className="flex items-center justify-between pt-3 border-t border-slate-50">
                  <div className="flex items-center space-x-1.5 font-mono text-[10px] font-bold">
                    <SafeIcon icon={FiClock} className="text-slate-400" />
                    <span className={sla.color}>SLA: {sla.text}</span>
                  </div>
                  <div className="text-[10px] font-black text-slate-300 uppercase tracking-widest">{ticket.id}</div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      <AnimatePresence>
        {showResetWarning && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[120] bg-slate-950/80 backdrop-blur-sm flex items-center justify-center p-4"
          >
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white p-8 rounded-[2.5rem] shadow-2xl max-w-sm w-full font-sans border border-slate-200"
            >
              <div className="flex justify-between items-start mb-6">
                <div className="w-12 h-12 bg-amber-100 rounded-2xl flex items-center justify-center text-amber-600">
                  <SafeIcon icon={FiAlertTriangle} className="text-2xl" />
                </div>
                <button onClick={() => setShowResetWarning(false)} className="text-slate-400 hover:text-slate-600">
                  <SafeIcon icon={FiX} />
                </button>
              </div>
              
              <h3 className="text-xl font-black text-slate-900 mb-2">Reset Session SLA?</h3>
              <p className="text-sm text-slate-500 leading-relaxed mb-8">
                This will restart the timers for all tickets in the <span className="font-bold text-slate-900 uppercase">{view}</span> queue. Resolved tickets in this tier will also be reopened. 
                <br /><br />
                <span className="text-red-500 font-bold uppercase text-[10px] tracking-widest">Warning:</span> This cannot be undone.
              </p>

              <div className="flex flex-col gap-3">
                <button 
                  onClick={handleConfirmReset}
                  className="w-full py-4 bg-slate-900 text-white rounded-2xl font-black uppercase tracking-widest text-[10px] shadow-xl hover:bg-slate-800 transition-all"
                >
                  Confirm Reset
                </button>
                <button 
                  onClick={() => setShowResetWarning(false)}
                  className="w-full py-3 text-xs font-black uppercase text-slate-400 hover:text-slate-600 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TicketQueue;